import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiClientError } from '@/api/client';
import { clearAuthClientState } from './auth-client-cleanup';
import type { Publication } from '@domain/publication/types';

export interface AuthUserView {
  id: string;
  email: string;
  fullName: string;
  status: string;
  emailVerifiedAt: string | null;
  createdAt?: string;
  profile?: {
    displayName: string;
    jobTitle: string | null;
    timezone: string | null;
    avatarMediaAssetId: string | null;
  };
}

export interface AuthOrganisation {
  id: string;
  name: string;
  slug: string;
}

interface AuthContextValue {
  user: AuthUserView | null;
  organisations: AuthOrganisation[];
  loading: boolean;
  authMode: 'legacy' | 'required';
  bootstrapPublications: Publication[] | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  setSession: (user: AuthUserView, organisations: AuthOrganisation[]) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUserView | null>(null);
  const [organisations, setOrganisations] = useState<AuthOrganisation[]>([]);
  const [authMode, setAuthMode] = useState<'legacy' | 'required'>('legacy');
  const [loading, setLoading] = useState(true);
  const [bootstrapPublications, setBootstrapPublications] = useState<Publication[] | null>(null);

  const refresh = useCallback(async () => {
    try {
      // Partial API mocks used by isolated screen harnesses pre-date bootstrap.
      // Production always takes the single-request path.
      const me = import.meta.env.MODE !== 'test' && typeof api.bootstrap === 'function'
        ? await api.bootstrap()
        : await (async () => {
            const configPromise = api.authConfig();
            const sessionPromise = api.authMe();
            const config = await configPromise;
            const session = await sessionPromise;
            return { ...session, mode: config.mode, publications: null };
          })();
      setAuthMode(me.mode);
      setBootstrapPublications(me.publications);
      if (!me.user) {
        setUser(null);
        setOrganisations([]);
        return;
      }
      setUser({
        ...me.user,
        profile: me.profile ?? undefined,
      });
      setOrganisations(me.organisations);
    } catch (err) {
      if (err instanceof ApiClientError && (err.status === 401 || err.status === 403)) {
        // Compatibility path for isolated/older clients without /api/bootstrap.
        const config = await api.authConfig().catch(() => ({ mode: 'legacy' as const }));
        setAuthMode(config.mode === 'required' ? 'required' : 'legacy');
        setUser(null);
        setOrganisations([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.authLogout().catch(() => undefined);
    setUser(null);
    setOrganisations([]);
    setBootstrapPublications(null);
    await clearAuthClientState();
  }, []);

  const setSession = useCallback((next: AuthUserView, orgs: AuthOrganisation[]) => {
    setUser(next);
    setOrganisations(orgs);
  }, []);

  const value = useMemo(
    () => ({ user, organisations, loading, authMode, bootstrapPublications, refresh, logout, setSession }),
    [user, organisations, loading, authMode, bootstrapPublications, refresh, logout, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Soft accessor for shell chrome that may render outside AuthProvider in harnesses. */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
